import "@/App.css";
import { lazy, Suspense, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { ScrollToTop } from "./components/ScrollToTop";
import { StatusShell } from "./components/status/StatusShell";
import { trackLinkClick } from "./lib/analytics";
import { isStatusHost } from "./lib/links";
import { ROUTES } from "./routes";

// One chunk per page: a visitor downloads only the page they open. A page
// whose chunk has already loaded renders at once, with no fallback: index.js
// loads the first page's chunk before rendering, so a prerendered page
// (scripts/prerender.js) is replaced by the same page, never by a blank.
const page = (load, name) => {
  let Loaded = null;
  const preload = () =>
    load().then((m) => {
      Loaded = m[name];
    });
  const Lazy = lazy(() => preload().then(() => ({ default: Loaded })));
  const Page = () => {
    // Fixed at mount, so a later render never swaps Lazy for Loaded and
    // remounts the page.
    const Component = useRef(Loaded || Lazy).current;
    return <Component />;
  };
  Page.preload = preload;
  return Page;
};

const PAGES = {
  HomePage: page(() => import("./pages/HomePage"), "HomePage"),
  ProductPage: page(() => import("./pages/ProductPage"), "ProductPage"),
  SecurityPage: page(() => import("./pages/SecurityPage"), "SecurityPage"),
  AIFeaturesPage: page(() => import("./pages/AIFeaturesPage"), "AIFeaturesPage"),
  UseCasesPage: page(() => import("./pages/UseCasesPage"), "UseCasesPage"),
  SolutionPage: page(() => import("./pages/SolutionPage"), "SolutionPage"),
  HowItWorksPage: page(() => import("./pages/HowItWorksPage"), "HowItWorksPage"),
  GuidesHubPage: page(() => import("./pages/GuidesHubPage"), "GuidesHubPage"),
  GuidePage: page(() => import("./pages/GuidePage"), "GuidePage"),
  GlossaryPage: page(() => import("./pages/GlossaryPage"), "GlossaryPage"),
  RequestInfoPage: page(() => import("./pages/RequestInfoPage"), "RequestInfoPage"),
  LoginRedirectPage: page(() => import("./pages/LoginRedirectPage"), "LoginRedirectPage"),
  SupportPage: page(() => import("./pages/SupportPage"), "SupportPage"),
  PrivacyPage: page(() => import("./pages/LegalPage"), "PrivacyPage"),
  TermsPage: page(() => import("./pages/LegalPage"), "TermsPage"),
  StatusPage: page(() => import("./pages/StatusPage"), "StatusPage"),
  NotFoundPage: page(() => import("./pages/NotFoundPage"), "NotFoundPage"),
};
const { StatusPage } = PAGES;

// The page a URL renders, so index.js can load its chunk before the first render.
const trimSlash = (pathname) => (pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname);
export const preloadPage = (pathname) => {
  if (isStatusHost()) return StatusPage.preload();
  const path = trimSlash(pathname);
  const route = ROUTES.find((r) => r.path === path) || ROUTES.find((r) => r.path === "*");
  return PAGES[route.page].preload();
};

// Development-only gallery of the product mocks (components/mock). The
// condition is a build-time constant, so production bundles drop the import.
const MockGallery =
  process.env.NODE_ENV !== "production"
    ? page(() => import("./components/mock/gallery/MockGallery"), "MockGallery")
    : null;

// status.diligenshub.com serves this same build (README "Status page"):
// there, every path is the status page, in its own frame.
const StatusHostRoutes = () => (
  <Routes>
    <Route
      path="*"
      element={
        <StatusShell>
          <Suspense fallback={<div className="min-h-screen" />}>
            <StatusPage />
          </Suspense>
        </StatusShell>
      }
    />
  </Routes>
);

const SiteRoutes = () => (
  <Routes>
    {ROUTES.map(({ path, page: name }) => {
      const Page = PAGES[name];
      return (
        <Route
          key={path}
          path={path}
          element={
            <Layout>
              <Suspense fallback={<div className="min-h-screen" />}>
                <Page />
              </Suspense>
            </Layout>
          }
        />
      );
    })}
    {MockGallery && (
      <Route
        path="/__mocks"
        element={
          <Suspense fallback={null}>
            <MockGallery />
          </Suspense>
        }
      />
    )}
  </Routes>
);

function App() {
  // Log In and email link clicks, wherever the link is (lib/analytics.js).
  useEffect(() => {
    document.addEventListener("click", trackLinkClick, true);
    return () => document.removeEventListener("click", trackLinkClick, true);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        {isStatusHost() ? <StatusHostRoutes /> : <SiteRoutes />}
      </BrowserRouter>
    </div>
  );
}

export default App;
